import React, { createContext, useContext, useState, useEffect } from 'react'
import { api } from '../services/api'
import { trackSignupConversion } from '../utils/adsConversion'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [socketToken, setSocketToken] = useState(null)
  const [showProfileCompletion, setShowProfileCompletion] = useState(false)

  // Check if profile needs completion — aligned with User model completionPercentage
  const checkProfileCompletion = (userData) => {
    if (!userData) return false

    if (userData.role === 'bedrijf') {
      const needsCompletion = !userData.companyName ||
                              !userData.contactPerson ||
                              !userData.website ||
                              !userData.companySize ||
                              !userData.companyBio ||
                              !userData.industry ||
                              !userData.location?.city
      return needsCompletion
    } else if (userData.role === 'agency') {
      const needsCompletion = !userData.agencyName ||
                              !userData.contactPerson ||
                              !userData.agencyBio ||
                              !userData.agencyLogo ||
                              !userData.location?.city ||
                              !userData.agencySize ||
                              !(userData.specializations?.length > 0)
      return needsCompletion
    } else if (userData.role === 'creator') {
      const needsCompletion = !userData.firstName ||
                              !userData.lastName ||
                              !userData.bio ||
                              !userData.profilePictureUrl ||
                              !userData.location?.city ||
                              !(userData.age || userData.dateOfBirth) ||
                              !(userData.niches?.length > 0) ||
                              !(userData.portfolio?.length > 0) ||
                              !userData.pricing?.baseRate ||
                              !(userData.socialMedia?.instagram?.username || userData.socialMedia?.tiktok?.username)
      return needsCompletion
    }
    return false
  }

  // Check if user is authenticated on mount (cookie is sent automatically)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await api.get('/auth/me')
        const userData = response.data.user
        setUser(userData)

        // Check if profile needs completion (Google OAuth users)
        if (checkProfileCompletion(userData)) {
          setShowProfileCompletion(true)
        }
      } catch (error) {
        // No valid cookie/session - user is not logged in
        setUser(null)
      }
      setLoading(false)
    }

    checkAuth()
  }, [])

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password })

      // Check if verification is required (2FA)
      if (response.data.requiresVerification) {
        return {
          success: true,
          requiresVerification: true,
          email: response.data.email,
          message: response.data.message
        }
      }

      // Legacy support: direct login without 2FA
      const { user: userData, socketToken: sToken } = response.data
      setUser(userData)
      if (sToken) setSocketToken(sToken)

      return { success: true, user: userData }
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  }

  const verifyLogin = async (email, code, type = 'login') => {
    try {
      const response = await api.post('/auth/verify-login', { email, code, type })
      const { user: userData, socketToken: sToken } = response.data

      // Cookie is set by backend automatically
      setUser(userData)
      if (sToken) setSocketToken(sToken)

      // Alleen bij het afronden van een registratie — niet bij inloggen.
      // Awaiten zodat het event weg is voordat de pagina doorstuurt.
      if (type === 'registration') {
        await trackSignupConversion(userData)
      }

      return { success: true, user: userData }
    } catch (error) {
      console.error('Verification error:', error)
      throw error
    }
  }

  const resendVerificationCode = async (email, type = 'login') => {
    try {
      const response = await api.post('/auth/resend-verification', { email, type })
      return { success: true, message: response.data.message }
    } catch (error) {
      console.error('Resend verification error:', error)
      throw error
    }
  }

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData)

      // Check if verification is required (2FA)
      if (response.data.requiresVerification) {
        return {
          success: true,
          requiresVerification: true,
          email: response.data.email,
          message: response.data.message
        }
      }

      // Legacy support: direct registration without 2FA (e.g., OAuth)
      const { user: newUser, socketToken: sToken } = response.data
      setUser(newUser)
      if (sToken) setSocketToken(sToken)

      return { success: true, user: newUser }
    } catch (error) {
      console.error('Registration error:', error)
      throw error
    }
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      // Ignore errors - clear local state anyway
    }
    setUser(null)
    setSocketToken(null)
  }

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }))
  }

  // Refresh user data from the server
  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me')
      const userData = response.data.user
      setUser(userData)
      return userData
    } catch (error) {
      console.error('Refresh user failed:', error)
      return null
    }
  }

  // For OAuth: just trigger a refresh since cookie is already set
  const handleOAuthSuccess = async () => {
    try {
      const response = await api.get('/auth/me')
      const userData = response.data.user
      setUser(userData)
      return { success: true, user: userData }
    } catch (error) {
      console.error('OAuth auth check failed:', error)
      return { success: false }
    }
  }

  const closeProfileCompletion = () => {
    setShowProfileCompletion(false)
  }

  const value = {
    user,
    loading,
    login,
    verifyLogin,
    resendVerificationCode,
    register,
    logout,
    updateUser,
    refreshUser,
    handleOAuthSuccess,
    socketToken,
    isAuthenticated: !!user,
    isCompany: user?.role === 'bedrijf' || user?.role === 'agency',
    isAgency: user?.role === 'agency',
    isCreator: user?.role === 'creator',
    showProfileCompletion,
    closeProfileCompletion,
    needsProfileCompletion: checkProfileCompletion(user)
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
